import React, { type ReactNode } from 'react';
import type { DrawerProps as AntdDrawerProps } from 'antd';
import { Drawer as AntdDrawer } from 'antd';
import { css, useTheme } from '@emotion/react';
import type { Env0Theme } from 'constants/themes.constants';

export type DrawerVariant = 'default' | 'flush' | 'tinted' | 'fullscreen-log';

export type DrawerProps = {
  open?: boolean;
  onClose?: AntdDrawerProps['onClose'];
  title?: ReactNode;
  placement?: AntdDrawerProps['placement'];
  width?: AntdDrawerProps['width'];
  height?: AntdDrawerProps['height'];
  closable?: boolean;
  destroyOnClose?: boolean;
  footer?: ReactNode;
  className?: string;
  /** Replaces the per-site `bodyStyle` / `headerStyle` / `footerStyle` inline styles (D7.4). */
  variant?: DrawerVariant;
  children?: ReactNode;
};

/**
 * The exact objects the call sites passed before this wrapper existed, so switch-off is unchanged by
 * construction. Legacy is also the only side allowed to read `Env0Theme` key names (D0).
 */
const legacyVariantStyles = (variant: DrawerVariant, theme: Env0Theme) =>
  ({
    default: {},
    flush: { bodyStyle: { padding: 0 } },
    tinted: {
      bodyStyle: { padding: 0 },
      headerStyle: { backgroundColor: theme.drawerHeaderBlue },
      footerStyle: { backgroundColor: theme.drawerHeaderBlue }
    },
    'fullscreen-log': { bodyStyle: { padding: 0, backgroundColor: theme.secondaryBlack } }
  })[variant];

/**
 * antd puts `className` on `.ant-drawer-content`, so these are descendant selectors - two classes
 * against antd's one, which is enough for everything below. Re-probe before adding a property.
 */
const newDesignVariant: Record<DrawerVariant, ReturnType<typeof css> | undefined> = {
  default: undefined,
  flush: css`
    .ant-drawer-body {
      padding: 0;
    }
  `,
  tinted: css`
    .ant-drawer-body {
      padding: 0;
    }

    .ant-drawer-header,
    .ant-drawer-footer {
      background-color: var(--surface-tint-purple);
    }
  `,
  'fullscreen-log': css`
    .ant-drawer-body {
      padding: 0;
      /* A terminal, not a page: this surface stays dark in light mode too. */
      background-color: var(--surface-navigation-navigation-item);
    }
  `
};

// Moved off antd-wrapper's <Global> block (APO-550). The legacy copy stays there, untouched.
const headerLayout = css`
  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }

  .ant-drawer-close {
    margin-inline-end: 0;
    margin-inline-start: 12px;
  }
`;

const Drawer: React.FC<DrawerProps> = ({ variant = 'default', className, children, ...props }) => {
  const theme = useTheme();

  return theme.isNewDesign ? (
    <AntdDrawer {...props} className={className} css={[headerLayout, newDesignVariant[variant]]}>
      {children}
    </AntdDrawer>
  ) : (
    <AntdDrawer {...props} className={className} {...legacyVariantStyles(variant, theme)}>
      {children}
    </AntdDrawer>
  );
};

export default Drawer;
